import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Package, Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BRAND_LOGO_URL, BRAND_NAME, SHOWCASE_IMAGES } from "@/lib/brand";
import { toast } from "sonner";

export default function Login() {
    const { login, user } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    React.useEffect(() => {
        if (user) {
            const dest = location.state?.from || "/";
            navigate(dest, { replace: true });
        }
    }, [user, navigate, location]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(email.trim(), password);
        setLoading(false);
        if (res.ok) {
            toast.success("Welcome back!");
            navigate(location.state?.from || "/", { replace: true });
        } else {
            toast.error(res.error || "Login failed");
        }
    };

    const signInWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2">
            {/* Left brand column */}
            <div
                className="hidden md:flex flex-col justify-between p-8 lg:p-10 text-white overflow-hidden relative"
                style={{ backgroundColor: "#0A0A0A" }}
                data-testid="login-brand-column"
            >
                <div className="flex items-center gap-3 relative z-10">
                    <div className="w-11 h-11 rounded-md bg-white flex items-center justify-center overflow-hidden shadow-sm">
                        <img src={BRAND_LOGO_URL} alt={BRAND_NAME} className="w-full h-full object-contain" />
                    </div>
                    <div>
                        <div className="text-lg font-bold font-display leading-none">BS Fiber Product</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-1">Stock Management ERP</div>
                    </div>
                </div>

                {/* Product showcase */}
                <div className="relative z-10 flex-1 flex flex-col justify-center py-8">
                    <div className="eyebrow text-neutral-500 mb-3">Signature Craft</div>
                    <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight font-display leading-[1.1] mb-6">
                        Every fountain,<br />
                        <span style={{ color: "#16A34A" }}>hand-fibered</span> to order.
                    </h1>

                    <div className="grid grid-cols-5 gap-3" data-testid="login-showcase">
                        {/* Hero image spans 3 cols */}
                        <div className="col-span-3 relative rounded-lg overflow-hidden border border-white/10 aspect-[4/5] group">
                            <img src={SHOWCASE_IMAGES[0].url} alt={SHOWCASE_IMAGES[0].title}
                                 className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                                <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-400">{SHOWCASE_IMAGES[0].caption}</div>
                                <div className="text-sm font-bold font-display text-white mt-0.5 leading-tight">{SHOWCASE_IMAGES[0].title}</div>
                            </div>
                        </div>
                        {/* Secondary image spans 2 cols */}
                        <div className="col-span-2 relative rounded-lg overflow-hidden border border-white/10 aspect-[4/5] group">
                            <img src={SHOWCASE_IMAGES[1].url} alt={SHOWCASE_IMAGES[1].title}
                                 className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                                <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-400">{SHOWCASE_IMAGES[1].caption}</div>
                                <div className="text-xs font-bold font-display text-white mt-0.5 leading-tight">{SHOWCASE_IMAGES[1].title}</div>
                            </div>
                        </div>
                    </div>

                    <p className="mt-5 text-neutral-400 max-w-md text-xs leading-relaxed">
                        Real-time stock, sales and purchase intelligence for fiber fountains, artificial
                        plants, pots and premium decor — engineered for the team behind every masterpiece.
                    </p>
                </div>

                <div className="text-xs text-neutral-500 flex items-center gap-2 relative z-10">
                    <Package size={14} weight="duotone" />
                    <span>© {new Date().getFullYear()} BS Fiber Product · Built for growers of green.</span>
                </div>
            </div>

            {/* Right form column */}
            <div className="flex items-center justify-center px-6 py-12 bg-white">
                <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
                    <div className="md:hidden flex items-center gap-3 mb-8">
                        <div className="w-11 h-11 rounded-md bg-white border border-neutral-200 flex items-center justify-center overflow-hidden">
                            <img src={BRAND_LOGO_URL} alt={BRAND_NAME} className="w-full h-full object-contain" />
                        </div>
                        <div className="text-lg font-bold font-display">BS Fiber Product</div>
                    </div>
                    <div className="hidden md:flex justify-center mb-6">
                        <img src={BRAND_LOGO_URL} alt={BRAND_NAME} className="w-32 h-32 object-contain" data-testid="login-logo" />
                    </div>
                    <div className="eyebrow mb-3">Welcome back</div>
                    <h2 className="text-3xl font-extrabold tracking-tight font-display mb-2">Sign in to your workspace</h2>
                    <p className="text-sm text-neutral-500 mb-8">Use your ERP credentials to continue.</p>

                    <div className="space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@bsfiber.com"
                                autoFocus
                                required
                                className="mt-1.5 h-11"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Password</Label>
                            <div className="relative mt-1.5">
                                <Input
                                    id="password"
                                    type={show ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="h-11 pr-11"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-neutral-500 hover:text-neutral-900"
                                    onClick={() => setShow((s) => !s)}
                                    tabIndex={-1}
                                    data-testid="login-password-toggle"
                                >
                                    {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-11 mt-8 text-sm font-semibold text-white"
                        style={{ backgroundColor: "#15803D" }}
                        data-testid="login-submit-btn"
                    >
                        {loading ? "Signing in..." : "Sign in"}
                    </Button>

                    {/* Divider */}
                    <div className="relative my-5">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200"></div></div>
                        <div className="relative flex justify-center"><span className="bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-neutral-500">or</span></div>
                    </div>

                    <button
                        type="button"
                        onClick={signInWithGoogle}
                        className="w-full h-11 border border-neutral-300 rounded-md flex items-center justify-center gap-2.5 text-sm font-semibold text-neutral-800 hover:bg-neutral-50 transition-colors"
                        data-testid="login-google-btn"
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.4 39.6 16.1 44 24 44z"/>
                            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41 34.5 44 29.6 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                        </svg>
                        Sign in with Google
                    </button>

                    <div className="mt-8 border-t border-neutral-200 pt-6 text-xs text-neutral-500 space-y-1.5">
                        <div className="font-semibold text-neutral-700">Demo credentials</div>
                        <div>Admin: <span className="font-mono text-neutral-700">bsfiberproduct@gmail.com</span> / <span className="font-mono">BSFiber@2026</span></div>
                        <div>Manager: <span className="font-mono text-neutral-700">manager@bsfiber.com</span> / <span className="font-mono">Manager@2026</span></div>
                        <div>Cashier: <span className="font-mono text-neutral-700">cashier@bsfiber.com</span> / <span className="font-mono">Cashier@2026</span></div>
                    </div>
                </form>
            </div>
        </div>
    );
}
